/**
 * @module builder
 */
import type {
  LegacyTieredPackagePriceDisplay,
  TieredPackageContentType,
  TieredPackageIcon,
  TieredPackagePriceDisplay,
  TieredPackageTier,
  TieredPackageTierId
} from 'paintscout'
import { TIER_ORDER } from './default-tiered-packages'

/**
 * Presentation tokens for package cards: the accent-color ramp, the font table, and the
 * icon set. Kept in the builder so the settings preview and the customer-facing card derive
 * identical colors from the same stored value.
 */

/**
 * The shape both a quote's `tieredPackages` and a Settings preset share, for the read-through
 * resolvers below. Colour and content type moved from the tier up to the package set; stored data
 * still carries the per-tier values, so every read goes through these.
 */
export interface TieredPackageStyleSource {
  color?: string
  contentType?: TieredPackageContentType
  tiers?: Record<TieredPackageTierId, TieredPackageTier>
}

/** The first value any tier carries, in display order — how the legacy per-tier fields resolve. */
function firstTierValue<T>(
  tiers: TieredPackageStyleSource['tiers'],
  read: (tier?: TieredPackageTier) => T | undefined
): T | undefined {
  for (const tierId of TIER_ORDER) {
    const value = read(tiers?.[tierId])
    if (value) {
      return value
    }
  }
  return undefined
}

/**
 * The package set's accent colour.
 *
 * Prefers the set-level value. Older sets stored a colour on each tier, and the Settings UI always
 * wrote the same value to all three, so the first tier carrying one is the set's colour.
 */
export function getTieredPackageSetColor(source?: TieredPackageStyleSource): string | undefined {
  return source?.color || firstTierValue(source?.tiers, (tier) => tier?.color)
}

/**
 * The package set's content type, defaulting to the feature list.
 *
 * Same read-through as the colour: the per-tier field is legacy and was always written uniformly.
 */
export function getTieredPackageContentType(source?: TieredPackageStyleSource): TieredPackageContentType {
  return source?.contentType || firstTierValue(source?.tiers, (tier) => tier?.contentType) || 'features'
}

/**
 * Normalise a stored price display.
 *
 * `'percentage'` was dropped when the display was cut to the three modes the design specifies.
 * Stored quotes and presets still hold it, so it folds into `'difference'` — both render the delta
 * against the reference tier, so the card keeps saying the same thing.
 */
export function getTieredPackagePriceDisplay(
  priceDisplay?: LegacyTieredPackagePriceDisplay
): TieredPackagePriceDisplay {
  if (priceDisplay === 'percentage') {
    return 'difference'
  }
  return priceDisplay ?? 'total'
}

export interface TieredPackageColor {
  key: string
  /** The stored swatch color. */
  base: string
  /**
   * For text, icons and the recommended button: the base color itself when it holds white text
   * legibly, otherwise the base darkened just far enough to do so.
   */
  dark: string
  /** Gradient stops for the recommended header, at the base color's own saturation. */
  vivid1: string
  vivid2: string
}

export const TIER_COLOR_SWATCHES: Array<{ key: string; base: string }> = [
  { key: 'blue', base: '#2377fd' },
  { key: 'indigo', base: '#4f46e5' },
  { key: 'teal', base: '#0f9d8f' },
  { key: 'green', base: '#16a34a' },
  { key: 'amber', base: '#d97706' },
  { key: 'rose', base: '#e11d48' },
  { key: 'purple', base: '#7c3aed' },
  { key: 'slate', base: '#475569' }
]

const HEX = /^#[0-9a-f]{6}$/i

function hexToRgb(hex: string) {
  const value = Number.parseInt(hex.replace('#', ''), 16)
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 }
}

function rgbToHex(r: number, g: number, b: number) {
  const part = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0')
  return `#${part(r)}${part(g)}${part(b)}`
}

function rgbToHsl(r: number, g: number, b: number) {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  const delta = max - min
  let h = 0
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    h = max === rn ? ((gn - bn) / delta) % 6 : max === gn ? (bn - rn) / delta + 2 : (rn - gn) / delta + 4
    h *= 60
    if (h < 0) {
      h += 360
    }
  }
  return { h, s, l }
}

function hslToRgb(h: number, s: number, l: number) {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let rgb: [number, number, number]
  if (h < 60) {
    rgb = [c, x, 0]
  } else if (h < 120) {
    rgb = [x, c, 0]
  } else if (h < 180) {
    rgb = [0, c, x]
  } else if (h < 240) {
    rgb = [0, x, c]
  } else if (h < 300) {
    rgb = [x, 0, c]
  } else {
    rgb = [c, 0, x]
  }
  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 }
}

/** Same color at a given alpha, for the tinted header/button fills. */
export function withAlpha(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** WCAG AA for normal text — the recommended button puts white text on `dark`. */
const MIN_CONTRAST_ON_WHITE = 4.5

/** WCAG relative luminance of an sRGB color. */
function relativeLuminance(r: number, g: number, b: number) {
  const channel = (n: number) => {
    const c = n / 255
    return c <= 0.039_28 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

function contrastOnWhite(hex: string) {
  const { r, g, b } = hexToRgb(hex)
  return 1.05 / (relativeLuminance(r, g, b) + 0.05)
}

/**
 * The darkest shade the cards need: the base itself if white text on it already passes, otherwise
 * the base with only its lightness lowered until it does.
 *
 * Hue and saturation are kept, so a contractor's pasted brand color renders as that color. The old
 * fixed step (saturation −18, lightness −16) was tuned for the vivid swatches and turned muted
 * brand colors — and the Slate swatch itself — grey.
 */
function darkShade(base: string): string {
  const { r, g, b } = hexToRgb(base)
  const { h, s, l } = rgbToHsl(r, g, b)
  let lightness = l
  let shade = base
  // Measured on the rounded hex, since that is the color that renders.
  while (lightness > 0 && contrastOnWhite(shade) < MIN_CONTRAST_ON_WHITE) {
    lightness = Math.max(0, lightness - 0.01)
    const rgb = hslToRgb(h, s, lightness)
    shade = rgbToHex(rgb.r, rgb.g, rgb.b)
  }
  return shade
}

/** Resolve a stored color (swatch key or #rrggbb) into the ramp the cards render with. */
export function getTieredPackageColor(color?: string): TieredPackageColor {
  const swatch = color && HEX.test(color) ? { key: color, base: color } : null
  const resolved = swatch ?? TIER_COLOR_SWATCHES.find((s) => s.key === color) ?? TIER_COLOR_SWATCHES[0]
  const { r, g, b } = hexToRgb(resolved.base)
  const { h, s } = rgbToHsl(r, g, b)
  // Scaled from the base's saturation rather than fixed, so a muted brand gets a muted header.
  const vivid1Rgb = hslToRgb(h, s * 0.85, 0.7)
  const vivid2Rgb = hslToRgb(h, s, 0.4)
  return {
    key: resolved.key,
    base: resolved.base,
    dark: darkShade(resolved.base),
    vivid1: rgbToHex(vivid1Rgb.r, vivid1Rgb.g, vivid1Rgb.b),
    vivid2: rgbToHex(vivid2Rgb.r, vivid2Rgb.g, vivid2Rgb.b)
  }
}

export interface TieredPackageFont {
  key: string
  label: string
  family: string
  /**
   * Webfontloader family spec (`Family:weights`) for FontLoad, or undefined for fonts that
   * need no loading (the theme default and system serifs).
   */
  webfont?: string
  /** Size used for the package name on the card. */
  cardSize: number
  /**
   * Kept so templates that already store it still render, but dropped from the Type row — six
   * chips plus the "All fonts" field is all the options bar fits.
   */
  legacy?: boolean
}

export const TIER_FONTS: TieredPackageFont[] = [
  { key: 'default', label: 'Default', family: 'inherit', cardSize: 18 },
  { key: 'roboto', label: 'Roboto', family: "'Roboto', sans-serif", webfont: 'Roboto:400,500', cardSize: 18 },
  {
    key: 'robotoMono',
    label: 'Roboto Mono',
    family: "'Roboto Mono', monospace",
    webfont: 'Roboto Mono:400,500',
    cardSize: 16
  },
  {
    key: 'workSans',
    label: 'Work Sans',
    family: "'Work Sans', sans-serif",
    webfont: 'Work Sans:400,500',
    cardSize: 18
  },
  {
    key: 'spectral',
    label: 'Spectral',
    family: "'Spectral', serif",
    webfont: 'Spectral:400,500',
    cardSize: 18,
    legacy: true
  },
  { key: 'karla', label: 'Karla', family: "'Karla', sans-serif", webfont: 'Karla:400,500', cardSize: 18 },
  { key: 'georgia', label: 'Georgia', family: 'Georgia, serif', cardSize: 18 }
]

/**
 * The font a stored value resolves to.
 *
 * Anything that is not one of the seven presets came from the "All fonts" picker, which stores a
 * CSS font stack (`'playfair display, serif'`). That is already a usable `font-family`, so it needs
 * no table entry — only the webfont, which the picker's own catalog resolves (the family label with
 * Google's casing lives there, not here).
 */
export const getTieredPackageFont = (key?: string): TieredPackageFont => {
  const preset = TIER_FONTS.find((f) => f.key === key)
  if (preset || !key) {
    return preset ?? TIER_FONTS[0]
  }
  return { key, label: key.split(',')[0].trim(), family: key, cardSize: 18 }
}

/** The fonts the Type row offers as chips. */
export const TIER_FONT_CHOICES = TIER_FONTS.filter((font) => !font.legacy)

/** Family specs to hand to `<FontLoad fonts={...}>` so the picker and cards render for real. */
export const TIER_FONT_WEBFONTS = TIER_FONTS.map((font) => font.webfont).filter(Boolean) as string[]

export const TIER_ICONS: Array<{ key: TieredPackageIcon; label: string }> = [
  { key: 'badge', label: 'Badge' },
  { key: 'badgeCheck', label: 'Badge check' },
  { key: 'star', label: 'Star' },
  { key: 'moonNew', label: 'Circle' },
  { key: 'sparkle', label: 'Sparkle' },
  { key: 'starOfLife', label: 'Star of life' },
  { key: 'certificate', label: 'Certificate' },
  { key: 'shield', label: 'Shield' }
]

export const TIER_BADGE_LABELS = ['Recommended', 'Most popular', 'Best Value']

/**
 * A longer custom badge label overflows the badge chip on the tier card, so the settings
 * inputs cap what the user can type.
 */
export const TIER_BADGE_LABEL_MAX_LENGTH = 20

/**
 * The customer-facing package name renders at 18px in a 200px-wide header pane on the
 * horizontal card layout, which fits about 13 characters per line. 20 sits comfortably inside
 * the two lines that pane has room for, and is the limit the builder prototype settled on.
 */
export const TIER_NAME_MAX_LENGTH = 20

/**
 * The tagline sits under the package name in the same pane, at a smaller size and across more
 * lines, so it has room for roughly three times the name. Long enough for a real sentence, short
 * enough that it stays a tagline rather than becoming the description below it.
 */
export const TIER_TAGLINE_MAX_LENGTH = 64

/**
 * Stand-in prices for the settings preview. A template has no estimate behind it, so the cards
 * show a worked example instead.
 *
 * Centred on the middle tier, because that is the tier a real estimate always represents: Basic
 * reads below it and Premium above it. Spaced evenly so the delta is the same figure either way,
 * which is what makes the Difference and Both displays legible at a glance.
 */
export const TIER_PREVIEW_PRICES: Record<TieredPackageTierId, number> = {
  low: 1250,
  mid: 1500,
  high: 1750
}

/**
 * Which tier the 'difference' and 'both' displays measure against: always Standard.
 *
 * Standard is the estimate as built. Every other tier's price comes from an adjustment off it, so
 * the delta a card shows is the adjustment the contractor typed. The anchor is deliberately not the
 * recommended tier: that badge moves by click and can be removed, and tying prices to it would
 * re-sign every other card — and move which card shows a plain total — on a presentational change.
 */
export const TIER_PRICE_REFERENCE_TIER_ID: TieredPackageTierId = 'mid'

/**
 * How one tier's price reads under a given display mode, split into the parts a card may wrap
 * between: `['$1,500']`, `['-$500']`, or for 'both' `['$1,500', '(-$500)']`. Callers that just want
 * a string can join it — see formatTierPriceDisplay.
 *
 * Takes the whole price map rather than a reference figure, so the anchor stays one rule in one
 * place. Callers cannot measure from a tier of their own choosing, which is how the settings
 * preview and the estimate drawer came to disagree.
 *
 * A zero delta always reads as the total, whatever the mode, so the anchor tier itself never shows
 * "+$0".
 */
export function formatTierPriceParts(args: {
  /** The tier being rendered. */
  tierId: TieredPackageTierId
  /** Every tier's price. The delta is measured from TIER_PRICE_REFERENCE_TIER_ID's entry. */
  prices: Partial<Record<TieredPackageTierId, number>>
  priceDisplay: LegacyTieredPackagePriceDisplay
  format: (value: number) => string
}): string[] {
  const { tierId, prices, format } = args
  const priceDisplay = getTieredPackagePriceDisplay(args.priceDisplay)
  const price = prices[tierId] ?? 0
  const delta = price - (prices[TIER_PRICE_REFERENCE_TIER_ID] ?? 0)

  if (!delta || priceDisplay === 'total') {
    return [format(price)]
  }

  const sign = delta > 0 ? '+' : '-'
  const difference = `${sign}${format(Math.abs(delta))}`

  if (priceDisplay === 'difference') {
    return [difference]
  }
  return [format(price), difference]
}

/** formatTierPriceParts as a single string, e.g. `$1,500 (+$500)`. */
export function formatTierPriceDisplay(args: Parameters<typeof formatTierPriceParts>[0]): string {
  return formatTierPriceParts(args).join(' ')
}

export interface TierPriceLines {
  /** The figure the card leads with. */
  main: string[]
  /** The second figure, or undefined when the mode shows only one. */
  sub?: string
  /**
   * Whether `sub` sits beside `main` or at the far end of the price row.
   *
   * 'both' reads as one phrase — `$1,500  +$500` — so the two stay together. 'difference' leads
   * with the delta and parks the real total at the right, where it reads as a footnote.
   */
  subInline?: boolean
}

/**
 * The same three modes as formatTierPriceParts, split into the two lines a package card renders.
 *
 * - `total`: the total, on its own.
 * - `difference`: the signed delta, with the total pushed to the far end of the row — the delta is
 *   the headline, but the customer still needs the real number.
 * - `both`: the total with the delta inline beside it.
 *
 * Anchored to Standard, exactly as formatTierPriceParts is — the drawer and the settings preview
 * read the same rule from TIER_PRICE_REFERENCE_TIER_ID.
 *
 * A zero delta always reads as the total alone, whatever the mode, so the anchor tier never shows
 * "+$0" or repeats itself.
 */
export function formatTierPriceLines(args: Parameters<typeof formatTierPriceParts>[0]): TierPriceLines {
  const { tierId, prices, format } = args
  const priceDisplay = getTieredPackagePriceDisplay(args.priceDisplay)
  const price = prices[tierId] ?? 0
  const delta = price - (prices[TIER_PRICE_REFERENCE_TIER_ID] ?? 0)

  if (!delta || priceDisplay === 'total') {
    return { main: [format(price)] }
  }

  const sign = delta > 0 ? '+' : '-'
  const difference = `${sign}${format(Math.abs(delta))}`

  if (priceDisplay === 'difference') {
    return { main: [difference], sub: format(price), subInline: false }
  }

  return { main: [format(price)], sub: difference, subInline: true }
}
